import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ButtonBase,
  Collapse,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { matchesDepartment } from './departments';
import { compareActivities, displayText, isDueThisWeek, phaseColor, workStatusText } from './helpers';
import { fieldHelp, help } from './helpTexts';
import { cardSx, chipButtonSx, launchColors } from './launchTheme';
import { ActivityProjection, Assignee, Workspace } from './types';
import ActivityRow from './ui/ActivityRow';
import InfoTip from './ui/InfoTip';

export type QuickFilter = '' | 'mine' | 'overdue' | 'week' | 'blocked' | 'undated';
export type SortKey = 'due' | 'title' | 'status' | 'phase';
export type GroupMode = 'urgency' | 'phase';

export interface WorkListFilters {
  q: string;
  phase: string;
  status: string;
  assignee: string;
  department: string;
  sort: SortKey;
  quick: QuickFilter;
  group: GroupMode;
}

export const workListFilterKey = 'nyhetslanseringar.worklistFilters.v1';
export const defaultWorkListFilters: WorkListFilters = {
  q: '',
  phase: '',
  status: '',
  assignee: '',
  department: '',
  sort: 'due',
  quick: '',
  group: 'urgency',
};

const quickFilters: Array<{ value: Exclude<QuickFilter, ''>; label: string }> = [
  { value: 'mine', label: 'Min avdelning' },
  { value: 'overdue', label: 'Försenade' },
  { value: 'week', label: 'Denna vecka' },
  { value: 'blocked', label: 'Blockerade' },
  { value: 'undated', label: 'Utan datum' },
];

type UrgencyGroup = 'blocked' | 'overdue' | 'risk' | 'week' | 'upcoming' | 'undated' | 'done' | 'excluded';

const urgencyGroups: Array<{ key: UrgencyGroup; label: string; color: string; collapsed?: boolean }> = [
  { key: 'blocked', label: 'Blockerat', color: launchColors.dangerText },
  { key: 'overdue', label: 'Försenat', color: launchColors.dangerText },
  { key: 'risk', label: 'Riskerar att bli sent', color: launchColors.plumDark },
  { key: 'week', label: 'Denna vecka', color: launchColors.warningText },
  { key: 'upcoming', label: 'Kommande', color: launchColors.navy },
  { key: 'undated', label: 'Utan datum', color: launchColors.textSecondary },
  { key: 'done', label: 'Klart', color: launchColors.mossDark, collapsed: true },
  { key: 'excluded', label: 'Utanför omfattning', color: launchColors.textSecondary, collapsed: true },
];

export const urgencyGroupOf = (activity: ActivityProjection, asOf: string): UrgencyGroup => {
  if (!activity.included) return 'excluded';
  if (activity.scheduleStatus === 'complete') return 'done';
  if (activity.activeBlocker) return 'blocked';
  if (activity.scheduleStatus === 'overdue') return 'overdue';
  if (activity.scheduleStatus === 'at_risk') return 'risk';
  if (activity.scheduleStatus === 'missing_due') return 'undated';
  if (isDueThisWeek(activity, asOf)) return 'week';
  return 'upcoming';
};

export const sortBySourceOrder = (items: ActivityProjection[], order: Map<string, number>): ActivityProjection[] => (
  [...items].sort((left, right) => (order.get(left.id) ?? 0) - (order.get(right.id) ?? 0))
);

export const filterWorkList = (
  activities: ActivityProjection[],
  filters: WorkListFilters,
  asOf: string,
  currentUserId: string,
  myDepartments: readonly string[] = [],
): ActivityProjection[] => {
  const q = filters.q.trim().toLocaleLowerCase('sv');
  return activities.filter((activity) => {
    if (q && !`${activity.title} ${displayText(activity.roleText)} ${activity.roleText}`.toLocaleLowerCase('sv').includes(q)) return false;
    if (filters.phase && activity.phaseKey !== filters.phase) return false;
    if (filters.status && activity.workStatus !== filters.status) return false;
    if (filters.assignee && activity.assigneeUserId !== filters.assignee) return false;
    if (filters.department && activity.roleText !== filters.department) return false;
    if (filters.quick === 'mine' && !(
      (Boolean(currentUserId) && activity.assigneeUserId === currentUserId) || matchesDepartment(activity.roleText, myDepartments)
    )) return false;
    if (filters.quick === 'overdue' && !(activity.included && activity.scheduleStatus === 'overdue')) return false;
    if (filters.quick === 'week' && !(activity.included && isDueThisWeek(activity, asOf))) return false;
    if (filters.quick === 'blocked' && !activity.activeBlocker) return false;
    if (filters.quick === 'undated' && !(activity.included && activity.scheduleStatus === 'missing_due')) return false;
    return true;
  }).sort((left, right) => {
    if (filters.sort === 'title') return left.title.localeCompare(right.title, 'sv');
    if (filters.sort === 'phase') return left.phaseKey.localeCompare(right.phaseKey, 'sv') || compareActivities(left, right);
    if (filters.sort === 'status') return left.workStatus.localeCompare(right.workStatus) || compareActivities(left, right);
    return compareActivities(left, right);
  });
};

interface Props {
  workspace: Workspace;
  assignees: Assignee[];
  currentUserId: string;
  myDepartments?: string[];
  filters: WorkListFilters;
  setFilters: React.Dispatch<React.SetStateAction<WorkListFilters>>;
  onOpenActivity: (id: string) => void;
}

const ActivityWorkList: React.FC<Props> = ({ workspace, assignees, currentUserId, myDepartments = [], filters, setFilters, onOpenActivity }) => {
  const asOf = workspace.timeline.asOf;
  const [moreOpen, setMoreOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>(() => Object.fromEntries(
    urgencyGroups.filter((group) => group.collapsed).map((group) => [group.key, true]),
  ));
  const names = useMemo(() => new Map(assignees.map((person) => [person.id, person.name])), [assignees]);
  const departments = useMemo(() => Array.from(new Set(workspace.activities.map((activity) => activity.roleText).filter(Boolean)))
    .sort((left, right) => displayText(left).localeCompare(displayText(right), 'sv')), [workspace.activities]);
  const assignedIds = useMemo(() => Array.from(new Set(workspace.activities.map((activity) => activity.assigneeUserId).filter((id): id is string => Boolean(id))))
    .sort((left, right) => (names.get(left) || left).localeCompare(names.get(right) || right, 'sv')), [names, workspace.activities]);
  const rows = useMemo(
    () => filterWorkList(workspace.activities, filters, asOf, currentUserId, myDepartments),
    [asOf, currentUserId, filters, myDepartments, workspace.activities],
  );
  const sourceOrder = useMemo(() => new Map(workspace.activities.map((activity, index) => [activity.id, index])), [workspace.activities]);

  const groups = useMemo(() => {
    if (filters.group === 'phase') {
      return workspace.phases.map((phase) => {
        const items = sortBySourceOrder(rows.filter((activity) => activity.phaseKey === phase.id), sourceOrder);
        return {
          key: phase.id,
          label: displayText(phase.name),
          color: phaseColor(phase.id),
          dot: true,
          summary: `${phase.doneCount} av ${phase.totalCount} klara`,
          items,
        };
      }).filter((group) => group.items.length > 0);
    }
    return urgencyGroups.map((group) => ({
      key: group.key,
      label: group.label,
      color: group.color,
      dot: false,
      summary: '',
      items: rows.filter((activity) => urgencyGroupOf(activity, asOf) === group.key),
    })).filter((group) => group.items.length > 0);
  }, [asOf, filters.group, rows, sourceOrder, workspace.phases]);

  const field = <K extends keyof WorkListFilters>(key: K, value: WorkListFilters[K]) => setFilters((current) => ({ ...current, [key]: value }));
  const advancedCount = [filters.phase, filters.status, filters.assignee, filters.department].filter(Boolean).length;
  const filtered = Boolean(filters.q || filters.quick || advancedCount);
  const tasks = rows.filter((activity) => activity.kind !== 'calendar_marker').length;
  const markers = rows.length - tasks;
  const totalTasks = workspace.activities.filter((activity) => activity.kind !== 'calendar_marker').length;

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} useFlexGap flexWrap="wrap">
        <TextField
          size="small"
          placeholder="Sök arbete eller avdelning"
          inputProps={{ 'aria-label': 'Sök arbete' }}
          value={filters.q}
          onChange={(event) => field('q', event.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          sx={{ minWidth: { md: 260 }, bgcolor: launchColors.card, borderRadius: 1 }}
        />
        <Stack direction="row" spacing={0.5} alignItems="center">
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" role="group" aria-label="Snabbfilter">
          {quickFilters.map((quick) => (
            <ButtonBase
              key={quick.value}
              aria-pressed={filters.quick === quick.value}
              onClick={() => field('quick', filters.quick === quick.value ? '' : quick.value)}
              sx={chipButtonSx(filters.quick === quick.value)}
            >
              {quick.label}
            </ButtonBase>
          ))}
          <Badge color="primary" badgeContent={advancedCount} invisible={!advancedCount}>
            <Button size="small" startIcon={<TuneIcon />} onClick={() => setMoreOpen((open) => !open)} aria-expanded={moreOpen}>Fler filter</Button>
          </Badge>
        </Stack>
        <InfoTip text={help.workFilters} />
        </Stack>
        <Box sx={{ flex: 1 }} />
        <ToggleButtonGroup
          size="small"
          exclusive
          value={filters.group}
          onChange={(_event, value: GroupMode | null) => value && field('group', value)}
          aria-label="Gruppera arbetslistan"
        >
          <ToggleButton value="urgency">Efter brådska</ToggleButton>
          <ToggleButton value="phase">Efter fas</ToggleButton>
        </ToggleButtonGroup>
        <InfoTip text={help.workGroup} />
      </Stack>

      <Collapse in={moreOpen} unmountOnExit>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} sx={[cardSx, { p: 1.5 }]}>
          <TextField size="small" select label="Fas" value={filters.phase} onChange={(event) => field('phase', event.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Alla faser</MenuItem>
            {workspace.phases.map((phase) => <MenuItem key={phase.id} value={phase.id}>{displayText(phase.name)}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Status" value={filters.status} onChange={(event) => field('status', event.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">Alla statusar</MenuItem>
            {Object.entries(workStatusText).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Person" value={filters.assignee} onChange={(event) => field('assignee', event.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">Alla personer</MenuItem>
            {assignedIds.map((id) => <MenuItem key={id} value={id}>{names.get(id) || id}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Roll" helperText={fieldHelp.roleFilter} value={filters.department} onChange={(event) => field('department', event.target.value)} sx={{ minWidth: 180 }}>
            <MenuItem value="">Alla roller</MenuItem>
            {departments.map((department) => <MenuItem key={department} value={department}>{displayText(department)}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Sortera" value={filters.sort} onChange={(event) => field('sort', event.target.value as SortKey)} sx={{ minWidth: 150 }}>
            <MenuItem value="due">Brådska och deadline</MenuItem>
            <MenuItem value="title">Namn</MenuItem>
            <MenuItem value="phase">Fas</MenuItem>
            <MenuItem value="status">Arbetsstatus</MenuItem>
          </TextField>
        </Stack>
      </Collapse>

      <Stack direction="row" spacing={1} alignItems="center">
        <InfoTip text={help.workLegend} />
        <InfoTip text={help.workCount} />
        <Typography sx={{ fontSize: 13, color: launchColors.textSecondary }} aria-live="polite">
          {filtered ? `Visar ${tasks} av ${totalTasks} uppgifter` : `${tasks} uppgifter`}
          {markers > 0 ? ` + ${markers} ${markers === 1 ? 'hållpunkt' : 'hållpunkter'}` : ''}
        </Typography>
        {filtered && (
          <Button size="small" onClick={() => setFilters((current) => ({ ...defaultWorkListFilters, sort: current.sort, group: current.group }))}>Rensa filter</Button>
        )}
      </Stack>

      {rows.length === 0 && <Alert severity="info">Inga aktiviteter matchar filtren.</Alert>}

      {groups.map((group) => {
        const isCollapsed = Boolean(collapsed[group.key]);
        return (
          <Box key={group.key} component="section" sx={[cardSx, { overflow: 'hidden' }]} aria-label={`${group.label}, ${group.items.length}`}>
            <ButtonBase
              onClick={() => setCollapsed((current) => ({ ...current, [group.key]: !isCollapsed }))}
              aria-expanded={!isCollapsed}
              sx={{
                width: '100%',
                justifyContent: 'flex-start',
                gap: 1,
                px: 1.75,
                py: 1.1,
                bgcolor: launchColors.section,
                borderBottom: isCollapsed ? 'none' : `1px solid ${launchColors.divider}`,
                borderLeft: group.dot ? `4px solid ${group.color}` : 'none',
                '&.Mui-focusVisible': { outline: `2px solid ${launchColors.navy}`, outlineOffset: -2 },
              }}
            >
              {isCollapsed ? <ChevronRightIcon fontSize="small" sx={{ color: launchColors.textSecondary }} /> : <ExpandMoreIcon fontSize="small" sx={{ color: launchColors.textSecondary }} />}
              <Typography component="span" sx={{ fontSize: 14, fontWeight: 700, color: group.dot ? launchColors.text : group.color }}>{group.label}</Typography>
              <Typography component="span" sx={{ fontSize: 12, fontWeight: 700, color: launchColors.textSecondary, bgcolor: launchColors.card, border: `1px solid ${launchColors.divider}`, borderRadius: '10px', px: 0.9, lineHeight: '18px' }}>{group.items.length}</Typography>
              {group.summary && <Typography component="span" sx={{ fontSize: 12, color: launchColors.textSecondary, ml: 'auto' }}>{group.summary}</Typography>}
            </ButtonBase>
            <Collapse in={!isCollapsed} unmountOnExit>
              <Box role="list" aria-label={group.label}>
                {group.items.map((activity) => (
                  <ActivityRow
                    key={activity.id}
                    activity={activity}
                    asOf={asOf}
                    onOpen={() => onOpenActivity(activity.id)}
                  />
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}
    </Stack>
  );
};

export default ActivityWorkList;
